/* Modal T4 — Feedback de visita (una o varias viviendas; gana la mejor calificación). */

import { useState, useEffect, useCallback, useMemo } from "react";
import api from "../services/api";
import { dialogs } from "../context/DialogContext";
import { useDraggableModalPanel } from "../hooks/useDraggableModalPanel";
import { parseTaskMetadata, getVisitAgendaId } from "../constants/leadFlowOutcomes";
import { TASK_UI } from "../constants/taskUiColors";
import { VISITA_RESULTADOS } from "../constants/visitaResultados";
import { parseEuroAmount } from "../utils/moneyParse";
import usePermiso from "../hooks/usePermiso";

function formatFechaVisitaES(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Madrid",
  });
}

const BTN_PRIMARY_FROM = "#1d4ed8";
const BTN_PRIMARY_TO = "#2563eb";

const GRUPO_TO_VISITA = {
  reservado: "cierre",
  sigue_proceso: "proceso",
  objeciones: "objeciones",
  descarte: "descarte",
};

const OPCION_PRIORITY = {
  Reservado: 100,
  "Pasa oferta": 90,
  "Segunda visita": 80,
  "Muy interesado": 70,
  "Precio alto": 50,
  "Necesita vender antes": 49,
  "Necesita financiación": 48,
  "No encaja": 20,
  "Necesita más reforma de lo esperado": 19,
  "Zona no convence": 18,
  "Ha encontrado otra": 17,
  "Ha pausado la búsqueda": 16,
};

const GRUPOS = [
  {
    key: "reservado",
    label: "CIERRE",
    titleColor: "#16a34a",
    bg: TASK_UI.whatsapp,
    color: TASK_UI.textOnBright,
    opciones: [{ codigo: "Reservado", label: "Reservado" }],
  },
  {
    key: "sigue_proceso",
    label: "SIGUE EL PROCESO",
    titleColor: "#ffffff",
    bg: TASK_UI.cita,
    color: "#ffffff",
    opciones: [
      { codigo: "Muy interesado", label: "Muy interesado" },
      { codigo: "Segunda visita", label: "Segunda visita" },
      { codigo: "Pasa oferta", label: "Pasa oferta" },
    ],
  },
  {
    key: "objeciones",
    label: "INTERÉS CON OBJECIONES",
    titleColor: "#d97706",
    bg: "#d97706",
    color: TASK_UI.textOnBright,
    opciones: [
      { codigo: "Precio alto", label: "Precio alto" },
      { codigo: "Necesita vender antes", label: "Necesita vender antes" },
      { codigo: "Necesita financiación", label: "Necesita financiación" },
    ],
  },
  {
    key: "descarte",
    label: "DESCARTE",
    titleColor: "#38bdf8",
    bg: "#38bdf8",
    color: TASK_UI.textOnBright,
    opciones: [
      { codigo: "No encaja", label: "No encaja" },
      { codigo: "Necesita más reforma de lo esperado", label: "Necesita más reforma de lo esperado" },
      { codigo: "Zona no convence", label: "Zona no convence" },
      { codigo: "Ha encontrado otra", label: "Ha encontrado otra" },
      { codigo: "Ha pausado la búsqueda", label: "Ha pausado la búsqueda" },
    ],
  },
];

/** Permiso granular (t_m2_*) de cada opción del modal M2, por código o por grupo. */
function m2OpcionNodeId(grupoKey, codigo) {
  switch (codigo) {
    case "Reservado":
      return "t_m2_reservado";
    case "Muy interesado":
      return "t_m2_muy_interesado";
    case "Segunda visita":
      return "t_m2_segunda_visita";
    case "Pasa oferta":
      return "t_m2_pasa_oferta";
    default:
      break;
  }
  if (grupoKey === "objeciones") return "t_m2_objeciones";
  if (grupoKey === "descarte") return "t_m2_descarte";
  return null;
}

function scoreForOpcion(codigo) {
  return VISITA_RESULTADOS.find((r) => r.valor === codigo)?.score ?? 0;
}

function aggregateSeleccion(seleccion, props) {
  const items = props
    .map((p) => {
      const pid = String(p.id);
      const sel = seleccion[pid];
      if (!sel?.grupoKey || !sel?.opcionCodigo) return null;
      return {
        propiedad_id: pid,
        referencia: p.referencia,
        grupoKey: sel.grupoKey,
        opcionCodigo: sel.opcionCodigo,
        score: scoreForOpcion(sel.opcionCodigo),
        observaciones: sel.observaciones || "",
      };
    })
    .filter(Boolean);
  if (!items.length) return null;
  const maxScore = Math.max(...items.map((x) => x.score));
  const candidates = items.filter((x) => x.score === maxScore);
  candidates.sort(
    (a, b) => (OPCION_PRIORITY[b.opcionCodigo] || 0) - (OPCION_PRIORITY[a.opcionCodigo] || 0)
  );
  return candidates[0];
}

function mapPropiedadVisitaRow(p) {
  const calle = String(
    p.calle ||
    [p.nombre_via, p.numero].filter(Boolean).join(" ") ||
    p.direccion ||
    ""
  ).trim();
  return {
    id: String(p.id),
    referencia: p.referencia || null,
    tipo_nombre: p.tipo_nombre || p.tipo || null,
    direccion: p.direccion || null,
    calle: calle || null,
    poblacion_nombre: p.poblacion_nombre || p.poblacion || null,
  };
}

function subtituloPropiedad(p) {
  const tipo = String(p.tipo_nombre || p.tipo || "").trim();
  const direccion = String(p.calle || p.direccion || "").trim();
  const poblacion = String(p.poblacion_nombre || p.poblacion || "").trim();
  return [tipo, direccion, poblacion].filter(Boolean).join(" · ");
}

export default function ModalResultadoVisita({
  isOpen,
  tarea,
  onClose,
  onCompleted,
  onSegundaVisita,
  theme,
}) {
  const c = theme?.colors || {};
  const isDark = theme?.name === "dark";
  const { dragRef, startDrag, panelPositionStyle } = useDraggableModalPanel();
  const { puede } = usePermiso();
  const puedeVerObservaciones = puede("t_m2_observaciones", "ver");
  const puedeModificarObservaciones = puede("t_m2_observaciones", "modificar");
  const puedeVerRegistrarOferta = puede("t_m2_registrar_oferta", "ver");
  const puedeModificarRegistrarOferta = puede("t_m2_registrar_oferta", "modificar");
  const puedeVerImporteSenal = puede("t_m2_importe_senal", "ver");
  const puedeModificarImporteSenal = puede("t_m2_importe_senal", "modificar");
  // Cada grupo/opción del resultado de visita se oculta por completo si su nodo t_m2_*
  // no tiene "ver" (por defecto todos son permisivos: comportamiento idéntico al actual).
  const gruposVisibles = GRUPOS
    .map((grupo) => ({
      ...grupo,
      opciones: grupo.opciones.filter((op) => puede(m2OpcionNodeId(grupo.key, op.codigo), "ver")),
    }))
    .filter((grupo) => grupo.opciones.length > 0);
  const [propsVisita, setPropsVisita] = useState([]);
  const [cargandoProps, setCargandoProps] = useState(false);
  const [tabActiva, setTabActiva] = useState(null);
  const [seleccion, setSeleccion] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [guardadoOk, setGuardadoOk] = useState(false);
  const [btnPressed, setBtnPressed] = useState(false);
  const [ofertaOpen, setOfertaOpen] = useState(false);
  const [ofImporte, setOfImporte] = useState("");
  const [ofTexto, setOfTexto] = useState("");
  const [reservadoOpen, setReservadoOpen] = useState(false);
  const [senalImporte, setSenalImporte] = useState("");

  const meta = useMemo(() => (tarea ? parseTaskMetadata(tarea) : {}), [tarea]);
  const taskCode = String(meta.task_code || "").toUpperCase();
  const fechaVisita = formatFechaVisitaES(meta.scheduled_start_at || meta.fecha_iso || meta.agenda_end_at);
  const leadId = tarea?.lead_id;
  const tareaId = tarea?.id;
  const multiProp = propsVisita.length > 1;

  const resetState = useCallback(() => {
    setSeleccion({});
    setTabActiva(null);
    setOfertaOpen(false);
    setOfImporte("");
    setOfTexto("");
    setReservadoOpen(false);
    setSenalImporte("");
    setGuardadoOk(false);
  }, []);

  // Carga al abrir / cambiar de tarea. NO depende de `tarea` refresheada por el
  // poll 10s de FichaCliente: eso reseteaba selección y «paradeaba» el modal.
  useEffect(() => {
    if (!isOpen || !tarea?.id) {
      setPropsVisita([]);
      resetState();
      return undefined;
    }
    let cancelled = false;
    resetState();
    setCargandoProps(true);

    const metaLocal = parseTaskMetadata(tarea);
    const agendaIdLocal = getVisitAgendaId(tarea);

    (async () => {
      try {
        if (agendaIdLocal) {
          const res = await api.get(`/agenda/${agendaIdLocal}/detalle-movil`);
          if (cancelled) return;
          const payload = res.data?.data ?? res.data ?? res;
          const lista = (payload.propiedades || []).map(mapPropiedadVisitaRow);
          if (lista.length) {
            setPropsVisita(lista);
            setTabActiva(lista[0].id);
            return;
          }
          const agRes = await api.get(`/agenda/${agendaIdLocal}`);
          if (cancelled) return;
          const citaRow = agRes.data?.data ?? agRes.data ?? {};
          const idsAgenda = Array.isArray(citaRow.propiedad_ids)
            ? citaRow.propiedad_ids.filter(Boolean).map(String)
            : [];
          if (idsAgenda.length) {
            const stubsAgenda = idsAgenda.map((id) => ({
              id,
              referencia: null,
              tipo_nombre: null,
              direccion: null,
            }));
            const enrichedAgenda = [];
            for (const stub of stubsAgenda) {
              try {
                const pr = await api.get(`/propiedades/${stub.id}`);
                const p = pr.data?.data || pr.data?.propiedad || pr.data || {};
                enrichedAgenda.push(mapPropiedadVisitaRow({ ...p, id: stub.id }));
              } catch {
                enrichedAgenda.push(stub);
              }
            }
            if (cancelled) return;
            setPropsVisita(enrichedAgenda);
            if (enrichedAgenda[0]?.id) setTabActiva(enrichedAgenda[0].id);
            return;
          }
        }
        const citaPropIds = Array.isArray(metaLocal.propiedad_ids)
          ? metaLocal.propiedad_ids.filter(Boolean)
          : [];
        const citaPropRefs = Array.isArray(metaLocal.propiedad_refs)
          ? metaLocal.propiedad_refs.filter(Boolean)
          : metaLocal.propiedad_ref
            ? [metaLocal.propiedad_ref]
            : [];
        const stubs = citaPropIds.length
          ? citaPropIds.map((id, i) => ({
              id: String(id),
              referencia: citaPropRefs[i] || null,
              tipo_nombre: null,
              direccion: null,
            }))
          : citaPropRefs.map((ref, i) => ({
              id: `ref-${i}`,
              referencia: ref,
              tipo_nombre: null,
              direccion: null,
            }));
        const enriched = [];
        for (const stub of stubs) {
          if (stub.id.startsWith("ref-")) {
            enriched.push(stub);
            continue;
          }
          try {
            const pr = await api.get(`/propiedades/${stub.id}`);
            const p = pr.data?.data || pr.data?.propiedad || pr.data || {};
            enriched.push(
              mapPropiedadVisitaRow({
                ...p,
                id: stub.id,
                referencia: p.referencia || stub.referencia,
              })
            );
          } catch {
            enriched.push(stub);
          }
        }
        if (cancelled) return;
        setPropsVisita(enriched);
        if (enriched[0]?.id) setTabActiva(enriched[0].id);
        else if (!enriched.length) {
          const fallback = {
            id: "__single__",
            referencia: metaLocal.propiedad_ref || citaPropRefs[0] || null,
            tipo_nombre: null,
            direccion: null,
          };
          setPropsVisita([fallback]);
          setTabActiva("__single__");
        }
      } catch (e) {
        if (!cancelled) {
          console.warn("[M2] cargar propiedades:", e.message);
          setPropsVisita([]);
        }
      } finally {
        if (!cancelled) setCargandoProps(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Solo al abrir o cambiar de tarea (id). El poll de ficha reescribe `tarea`
    // cada 10s con el mismo id y no debe vaciar la cualificación en curso.
  }, [isOpen, tarea?.id, resetState]);

  if (!isOpen || !tarea?.id) return null;

  const pidActivo = tabActiva || propsVisita[0]?.id || null;
  const propActiva = propsVisita.find((p) => p.id === pidActivo) || propsVisita[0] || null;
  const selActiva = pidActivo ? seleccion[pidActivo] || {} : {};
  const ganador = propsVisita.length ? aggregateSeleccion(seleccion, propsVisita) : null;

  const todasCualificadas =
    propsVisita.length > 0 &&
    propsVisita.every((p) => seleccion[p.id]?.grupoKey && seleccion[p.id]?.opcionCodigo);

  const listoSingle = Boolean(selActiva.grupoKey && selActiva.opcionCodigo);
  const listoParaGuardar = propsVisita.length > 0 ? todasCualificadas : listoSingle;
  const puedeGuardar = listoParaGuardar && !guardando && !guardadoOk && !cargandoProps;

  function seleccionar(grupoKey, opcionCodigo) {
    if (!pidActivo) return;
    setSeleccion((prev) => ({
      ...prev,
      [pidActivo]: {
        ...(prev[pidActivo] || {}),
        grupoKey,
        opcionCodigo,
        observaciones: prev[pidActivo]?.observaciones || "",
      },
    }));
  }

  function setObsActiva(text) {
    if (!pidActivo) return;
    setSeleccion((prev) => ({
      ...prev,
      [pidActivo]: { ...(prev[pidActivo] || {}), observaciones: text },
    }));
  }

  function buildCalificacionesPayload() {
    return propsVisita.map((p) => ({
      propiedad_id: p.id.startsWith("ref-") ? null : p.id,
      calificacion: seleccion[p.id]?.opcionCodigo,
      grupo: GRUPO_TO_VISITA[seleccion[p.id]?.grupoKey] || seleccion[p.id]?.grupoKey,
      observaciones: seleccion[p.id]?.observaciones || "",
      referencia: p.referencia,
    })).filter((row) => row.propiedad_id && row.calificacion);
  }

  function buildSinglePayload() {
    const grupoKey = selActiva.grupoKey;
    const opcion = selActiva.opcionCodigo;
    const obs = (selActiva.observaciones || "").trim();
    return {
      accion: grupoKey,
      resultado: obs ? `${opcion} — ${obs}` : opcion,
    };
  }

  async function handleCerrar() {
    if (guardando || guardadoOk) return;
    const ok = await dialogs.confirm(
      "¿Cerrar sin guardar? El resultado de la visita que hayas indicado se perderá."
    );
    if (!ok) return;
    onClose?.();
  }

  async function cerrarAhoraNo() {
    if (guardando) return;
    setGuardando(true);
    try {
      await api.post(`/tareas/lead/${leadId}/feedback-visita`, {
        accion: "ahora_no",
        tarea_id: tareaId,
      });
      onClose?.();
    } catch (e) {
      await dialogs.alert(e.response?.data?.error || e.message || "No se pudo posponer el feedback");
    } finally {
      setGuardando(false);
    }
  }

  async function enviarFeedback(extra = null) {
    if (guardando) return;
    setGuardando(true);
    try {
      let body;
      if (propsVisita.length > 0 && propsVisita.every((p) => !p.id.startsWith("ref-"))) {
        body = {
          tarea_id: tareaId,
          calificaciones: buildCalificacionesPayload(),
        };
      } else {
        body = { tarea_id: tareaId, ...buildSinglePayload() };
      }
      if (extra?.oferta) body.oferta = extra.oferta;
      if (extra?.senal) body.senal = extra.senal;
      const res = await api.post(`/tareas/lead/${leadId}/feedback-visita`, body);
      setOfertaOpen(false);
      setReservadoOpen(false);
      setGuardadoOk(true);
      await new Promise((r) => setTimeout(r, 900));
      await onCompleted?.(res.data || res);
      onClose?.();
    } catch (e) {
      await dialogs.alert(e.response?.data?.error || e.message || "No se pudo registrar el feedback");
    } finally {
      setGuardando(false);
    }
  }

  async function guardar() {
    if (!listoParaGuardar) {
      await dialogs.alert(
        propsVisita.length > 1
          ? "Cualifica todas las viviendas de la visita antes de guardar."
          : "Selecciona el resultado de la visita antes de guardar."
      );
      return;
    }

    const win = ganador || {
      grupoKey: selActiva.grupoKey,
      opcionCodigo: selActiva.opcionCodigo,
      propiedad_id: propActiva?.id,
    };

    if (
      win.grupoKey === "sigue_proceso" &&
      win.opcionCodigo === "Segunda visita" &&
      typeof onSegundaVisita === "function"
    ) {
      // Solo las viviendas cualificadas como «Segunda visita» (no las descartadas).
      const propiedadIdsSegunda = Object.entries(seleccion || {})
        .filter(([, s]) => s?.opcionCodigo === "Segunda visita")
        .map(([pid]) => String(pid))
        .filter((pid) => pid && !pid.startsWith("ref-"));
      const propiedadId =
        (win.propiedad_id && !String(win.propiedad_id).startsWith("ref-")
          ? String(win.propiedad_id)
          : null)
        || propiedadIdsSegunda[0]
        || null;
      onSegundaVisita(tarea, {
        observaciones: win.observaciones || selActiva.observaciones || "",
        propiedad_id: propiedadId,
        propiedad_ids: propiedadIdsSegunda.length
          ? propiedadIdsSegunda
          : (propiedadId ? [propiedadId] : []),
        // Todas las califs del modal (incl. descartes): el POST de segunda visita las persiste.
        calificaciones: buildCalificacionesPayload(),
      });
      onClose?.();
      return;
    }

    if (win.grupoKey === "sigue_proceso" && win.opcionCodigo === "Pasa oferta") {
      if (puedeVerRegistrarOferta) {
        setOfertaOpen(true);
        return;
      }
      // Sin permiso de ver el sub-modal "Registrar oferta": se guarda el resultado igual,
      // simplemente sin el paso adicional de importe/detalle de oferta.
      await enviarFeedback();
      return;
    }

    if (win.grupoKey === "reservado") {
      if (puedeVerImporteSenal) {
        setReservadoOpen(true);
        return;
      }
      await enviarFeedback();
      return;
    }

    await enviarFeedback();
  }

  async function confirmarOferta() {
    const propId = ganador?.propiedad_id || propActiva?.id || null;
    await enviarFeedback({
      oferta: {
        importe: ofImporte.trim() || null,
        texto: ofTexto.trim() || null,
        propiedad_id: propId && !String(propId).startsWith("ref-") ? propId : null,
      },
    });
  }

  async function confirmarReservado() {
    const raw = senalImporte.trim();
    if (!raw) {
      await dialogs.alert("Indica el importe de la señal.");
      return;
    }
    const importeNum = parseEuroAmount(raw);
    if (!Number.isFinite(importeNum) || importeNum <= 0) {
      await dialogs.alert("Importe de señal no válido.");
      return;
    }
    await enviarFeedback({ senal: { importe: importeNum } });
  }

  const overlay = {
    position: "fixed",
    inset: 0,
    zIndex: 1300,
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  };

  const panel = {
    background: isDark ? "#1e293b" : "#fff",
    borderRadius: 16,
    padding: "24px 24px 20px",
    width: "100%",
    maxWidth: multiProp ? 620 : 540,
    maxHeight: "90vh",
    overflow: "auto",
    border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
    boxShadow: isDark ? "0 24px 64px rgba(0,0,0,0.6)" : "0 20px 48px rgba(15,23,42,0.18)",
  };

  return (
    <div style={overlay}>
      <div
        ref={dragRef}
        onClick={(e) => e.stopPropagation()}
        style={{ ...panel, ...panelPositionStyle }}
      >
        <div
          onMouseDown={startDrag}
          style={{
            marginBottom: 16,
            position: "relative",
            paddingRight: 36,
            cursor: "grab",
            userSelect: "none",
          }}
        >
          <button
            type="button"
            onClick={handleCerrar}
            disabled={guardando || guardadoOk}
            aria-label="Cerrar"
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 32,
              height: 32,
              borderRadius: 8,
              border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
              background: isDark ? "#0f172a" : "#f8fafc",
              cursor: guardando || guardadoOk ? "not-allowed" : "pointer",
              fontSize: 16,
              color: isDark ? "#94a3b8" : "#64748b",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              color: isDark ? "#64748b" : "#94a3b8",
              marginBottom: 4,
            }}
          >
            {taskCode === "TC" ? "T4 · CITA" : "T4 · FEEDBACK DE VISITA"}
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: isDark ? "#f1f5f9" : "#0f172a", lineHeight: 1.2 }}>
            Resultado de la visita
          </div>
          {fechaVisita && (
            <div style={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b", marginTop: 6 }}>
              Visita: {fechaVisita}
            </div>
          )}
        </div>

        {cargandoProps && (
          <div style={{ fontSize: 13, color: c.textMuted, marginBottom: 12 }}>Cargando viviendas…</div>
        )}

        {!cargandoProps && multiProp && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              marginBottom: 14,
              borderBottom: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
              paddingBottom: 12,
            }}
          >
            {propsVisita.map((p) => {
              const activa = p.id === pidActivo;
              const ok = Boolean(seleccion[p.id]?.opcionCodigo);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setTabActiva(p.id)}
                  disabled={guardando}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 8,
                    border: activa ? "2px solid #2563eb" : `1px solid ${c.border || "#cbd5e1"}`,
                    background: activa ? (isDark ? "#1e3a5f" : "#eff6ff") : "transparent",
                    color: c.textPrimary,
                    fontSize: 12,
                    fontWeight: activa ? 700 : 600,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    whiteSpace: "nowrap",
                  }}
                >
                  {p.referencia || "—"}
                  {ok ? " ✓" : ""}
                </button>
              );
            })}
          </div>
        )}

        {!cargandoProps && propActiva && (
          <>
            {!multiProp && propActiva.referencia && (
              <div style={{ fontSize: 15, fontWeight: 800, color: c.textPrimary, marginBottom: 4 }}>
                Ref. {propActiva.referencia}
              </div>
            )}
            {subtituloPropiedad(propActiva) && (
              <div style={{ fontSize: 13, color: c.textSecondary || c.textMuted, marginBottom: 16 }}>
                {subtituloPropiedad(propActiva)}
              </div>
            )}
          </>
        )}

        {gruposVisibles.map((grupo) => (
          <div key={grupo.key} style={{ marginBottom: 16 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.06em",
                color: grupo.key === "sigue_proceso" && !isDark ? "#2563eb" : grupo.titleColor,
                marginBottom: 8,
              }}
            >
              {grupo.label}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {grupo.opciones.map((op) => {
                const isSelected =
                  selActiva.grupoKey === grupo.key && selActiva.opcionCodigo === op.codigo;
                return (
                  <button
                    key={op.codigo}
                    type="button"
                    onClick={() => seleccionar(grupo.key, op.codigo)}
                    disabled={guardando || cargandoProps}
                    style={{
                      padding: "8px 16px",
                      borderRadius: 999,
                      border: isSelected ? "2px solid #ffffff" : "2px solid transparent",
                      background: grupo.bg,
                      color: grupo.color,
                      fontSize: 13,
                      fontWeight: isSelected ? 700 : 600,
                      cursor: guardando ? "not-allowed" : "pointer",
                      opacity: guardando ? 0.7 : 1,
                    }}
                  >
                    {op.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {puedeVerObservaciones && (
          <div style={{ marginTop: 4, marginBottom: 16 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.06em",
                color: isDark ? "#ffffff" : c.textPrimary || "#0f172a",
                marginBottom: 6,
                textTransform: "uppercase",
              }}
            >
              Observaciones
              {multiProp && propActiva?.referencia ? ` (${propActiva.referencia})` : ""}
            </div>
            <textarea
              value={selActiva.observaciones || ""}
              onChange={(e) => setObsActiva(e.target.value)}
              placeholder="Notas sobre el interés del cliente en esta vivienda…"
              rows={3}
              disabled={guardando || !puedeModificarObservaciones}
              title={!puedeModificarObservaciones ? "No tienes permiso para modificar las observaciones" : undefined}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: `1px solid ${isDark ? "#334155" : "#cbd5e1"}`,
                background: isDark ? "#0f172a" : "#f8fafc",
                color: isDark ? "#e2e8f0" : "#0f172a",
                fontSize: 13,
                resize: "vertical",
                fontFamily: "inherit",
                boxSizing: "border-box",
                opacity: puedeModificarObservaciones ? 1 : 0.6,
                cursor: puedeModificarObservaciones ? "text" : "not-allowed",
              }}
            />
          </div>
        )}

        {multiProp && ganador && todasCualificadas && (
          <div
            style={{
              fontSize: 12,
              color: c.textMuted,
              marginBottom: 14,
              padding: "10px 12px",
              borderRadius: 8,
              background: isDark ? "rgba(255,255,255,0.04)" : "#f1f5f9",
            }}
          >
            Siguiente tarea según la <strong>mejor</strong> calificación:{" "}
            <strong>{ganador.opcionCodigo}</strong>
            {ganador.referencia ? ` (${ganador.referencia})` : ""}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button
            type="button"
            onClick={guardar}
            disabled={!puedeGuardar}
            onMouseDown={() => puedeGuardar && setBtnPressed(true)}
            onMouseUp={() => setBtnPressed(false)}
            onMouseLeave={() => setBtnPressed(false)}
            style={{
              flex: 1,
              padding: "12px 20px",
              borderRadius: 10,
              border: "none",
              background: guardadoOk
                ? "linear-gradient(135deg, #16a34a, #22c55e)"
                : `linear-gradient(135deg, ${BTN_PRIMARY_FROM}, ${BTN_PRIMARY_TO})`,
              color: "#ffffff",
              fontSize: 15,
              fontWeight: 700,
              cursor: puedeGuardar ? "pointer" : "not-allowed",
              opacity: listoParaGuardar || guardando || guardadoOk ? 1 : 0.72,
              transform: btnPressed ? "scale(0.97)" : "scale(1)",
            }}
          >
            {guardadoOk ? "Guardado ✓" : guardando ? "Guardando…" : "Guardar resultado"}
          </button>
          <button
            type="button"
            onClick={cerrarAhoraNo}
            disabled={guardando}
            style={{
              background: "transparent",
              border: `1px solid ${isDark ? "#ffffff" : "#94a3b8"}`,
              borderRadius: 10,
              color: isDark ? "#e2e8f0" : "#64748b",
              fontSize: 13,
              padding: "10px 16px",
              cursor: guardando ? "not-allowed" : "pointer",
            }}
          >
            Ahora no
          </button>
        </div>
      </div>

      {ofertaOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1400,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            style={{
              background: isDark ? "#1e293b" : "#fff",
              borderRadius: 16,
              padding: "22px 22px 18px",
              width: "100%",
              maxWidth: 440,
              border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
              position: "relative",
            }}
          >
            <button
              type="button"
              onClick={() => !guardando && setOfertaOpen(false)}
              disabled={guardando}
              aria-label="Cerrar"
              style={{
                position: "absolute",
                top: 14,
                right: 14,
                width: 32,
                height: 32,
                borderRadius: 8,
                border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                background: isDark ? "#0f172a" : "#f8fafc",
                cursor: guardando ? "not-allowed" : "pointer",
                fontSize: 16,
                color: isDark ? "#94a3b8" : "#64748b",
              }}
            >
              ✕
            </button>
            <div style={{ fontSize: 18, fontWeight: 800, color: c.textPrimary, marginBottom: 16 }}>
              Registrar oferta
              {ganador?.referencia ? ` · Ref. ${ganador.referencia}` : ""}
            </div>
            <input
              type="text"
              value={ofImporte}
              onChange={(e) => setOfImporte(e.target.value)}
              placeholder="Importe (€)"
              disabled={guardando || !puedeModificarRegistrarOferta}
              style={{
                width: "100%",
                marginBottom: 12,
                padding: "10px 12px",
                borderRadius: 8,
                border: `1px solid ${c.border}`,
                boxSizing: "border-box",
                opacity: puedeModificarRegistrarOferta ? 1 : 0.6,
              }}
            />
            <textarea
              value={ofTexto}
              onChange={(e) => setOfTexto(e.target.value)}
              placeholder="Detalles de la oferta…"
              rows={4}
              disabled={guardando || !puedeModificarRegistrarOferta}
              style={{
                width: "100%",
                marginBottom: 16,
                padding: "10px 12px",
                borderRadius: 8,
                border: `1px solid ${c.border}`,
                boxSizing: "border-box",
                fontFamily: "inherit",
                opacity: puedeModificarRegistrarOferta ? 1 : 0.6,
              }}
            />
            <button
              type="button"
              onClick={confirmarOferta}
              disabled={guardando || !puedeModificarRegistrarOferta}
              title={!puedeModificarRegistrarOferta ? "No tienes permiso para registrar la oferta" : undefined}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: 10,
                border: "none",
                background: `linear-gradient(135deg, ${BTN_PRIMARY_FROM}, ${BTN_PRIMARY_TO})`,
                color: "#fff",
                fontWeight: 700,
                cursor: guardando || !puedeModificarRegistrarOferta ? "not-allowed" : "pointer",
              }}
            >
              {guardando ? "Guardando…" : "Registrar oferta y crear tarea"}
            </button>
          </div>
        </div>
      )}

      {reservadoOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1400,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            style={{
              background: isDark ? "#1e293b" : "#fff",
              borderRadius: 16,
              padding: "22px 22px 18px",
              width: "100%",
              maxWidth: 440,
              border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
              position: "relative",
            }}
          >
            <button
              type="button"
              onClick={() => !guardando && setReservadoOpen(false)}
              disabled={guardando}
              aria-label="Cerrar"
              style={{
                position: "absolute",
                top: 14,
                right: 14,
                width: 32,
                height: 32,
                borderRadius: 8,
                border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                background: isDark ? "#0f172a" : "#f8fafc",
                cursor: guardando ? "not-allowed" : "pointer",
                fontSize: 16,
                color: isDark ? "#94a3b8" : "#64748b",
              }}
            >
              ✕
            </button>
            <div style={{ fontSize: 18, fontWeight: 800, color: c.textPrimary, marginBottom: 8 }}>
              Importe de la señal
              {ganador?.referencia ? ` · Ref. ${ganador.referencia}` : ""}
            </div>
            <div style={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b", marginBottom: 16 }}>
              Se creará el expediente con esta señal como primera anotación.
            </div>
            <input
              type="text"
              value={senalImporte}
              onChange={(e) => setSenalImporte(e.target.value)}
              placeholder="Importe (€)"
              disabled={guardando || !puedeModificarImporteSenal}
              autoFocus
              style={{
                width: "100%",
                marginBottom: 16,
                padding: "10px 12px",
                borderRadius: 8,
                border: `1px solid ${c.border}`,
                boxSizing: "border-box",
                textAlign: "right",
                opacity: puedeModificarImporteSenal ? 1 : 0.6,
              }}
            />
            <button
              type="button"
              onClick={confirmarReservado}
              disabled={guardando || !puedeModificarImporteSenal}
              title={!puedeModificarImporteSenal ? "No tienes permiso para registrar el importe de la señal" : undefined}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: 10,
                border: "none",
                background: `linear-gradient(135deg, ${BTN_PRIMARY_FROM}, ${BTN_PRIMARY_TO})`,
                color: "#fff",
                fontWeight: 700,
                cursor: guardando || !puedeModificarImporteSenal ? "not-allowed" : "pointer",
              }}
            >
              {guardando ? "Guardando…" : "Confirmar reserva y crear expediente"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
